import { Pencil } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import type { IssueCategory } from '../online-eval.types';

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleString() : 'Never';

export const CategoriesTable = ({
  categories,
  onEdit,
  onDeprecatedChange,
}: {
  categories: IssueCategory[];
  onEdit: (category: IssueCategory) => void;
  onDeprecatedChange: (category: IssueCategory, deprecated: boolean) => void;
}) => (
  <div className="rounded-lg border bg-card">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead className="min-w-80">Description</TableHead>
          <TableHead className="text-right">Findings</TableHead>
          <TableHead className="text-right">Users</TableHead>
          <TableHead>Last flagged</TableHead>
          <TableHead>Created / updated</TableHead>
          <TableHead className="text-center">Active</TableHead>
          <TableHead className="w-16" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {categories.map((category) => (
          <TableRow key={category.id}>
            <TableCell className="font-mono text-xs font-medium">
              <div className="flex items-center gap-2">
                {category.name}
                {category.deprecated && (
                  <Badge variant="secondary" className="font-sans">
                    Deprecated
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell className="max-w-lg">{category.description}</TableCell>
            <TableCell className="text-right tabular-nums">
              {category.finding_count.toLocaleString()}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {category.affected_user_count.toLocaleString()}
            </TableCell>
            <TableCell className="whitespace-nowrap text-xs">
              {formatDate(category.last_flagged_at)}
            </TableCell>
            <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
              <div>{formatDate(category.created_at)}</div>
              <div>{formatDate(category.updated_at)}</div>
            </TableCell>
            <TableCell className="text-center">
              <Switch
                checked={!category.deprecated}
                onCheckedChange={(active) =>
                  onDeprecatedChange(category, !active)
                }
                aria-label={`Include ${category.name} in new evaluation runs`}
              />
            </TableCell>
            <TableCell>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit(category)}
                aria-label={`Edit ${category.name}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);
